import { PDFPageProxy } from 'pdfjs-dist';
import React from 'react';
import { ListOnScrollProps, VariableSizeList } from 'react-window';
import Page from './Page';
import PdfPage from './PdfPage';
import '../Viewer.module.scss';

type ViewerProps = {
  pages: PDFPageProxy[];
  setPages: React.Dispatch<React.SetStateAction<PDFPageProxy[]>>;
  pagesCount: number;
  handleGetPdfPage: (idx: any) => Promise<PDFPageProxy>;
  windowRef: React.RefObject<VariableSizeList>;
  width: string | number;
  height: string | number;
  gap?: number;
  scale: number;
  setScale: React.Dispatch<React.SetStateAction<number>>;
  setIsAvailableAccept?: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentPage?:  React.Dispatch<React.SetStateAction<number>>;
};

const Viewer: React.FC<ViewerProps> = ({
  pages,
  setPages,
  pagesCount,
  handleGetPdfPage,
  windowRef,
  scale,
  height = 400,
  setScale,
  width,
  gap = 10, // MS Word 
  setIsAvailableAccept,
  setCurrentPage
}) => {
  const [overallHeight, setOverallHeight] = React.useState(0);
  const [isFlexStart, setIsFlexStart] = React.useState(false);
  const [pagesIntervals, setPagesIntervals] = React.useState<number[][]>([]);
  
  React.useEffect(() => {
    windowRef.current!.resetAfterIndex(0);
  }, [scale]);

  React.useEffect(() => {
    const viewport = pages[0]?.getViewport({ scale });
    const itemSize = viewport?.height + gap;
    if (viewport) {
      if (itemSize > (Number(width) * 1.4)) setIsFlexStart(true);
      else setIsFlexStart(false);
    }
  }, [width, scale]);

  React.useEffect(() => {
    if (pages[0]) {
      const heightOfparent = Number(windowRef.current?.props.height);
      let documentHeight: number = 0;

      const pagesHeightIntervals = pages.reduce((accumulator: number[][], page, i) => {
        const heightOfPage = page?.getViewport({ scale })?.height;
        const prevHeight = accumulator[i - 1] ? accumulator[i - 1][1] : 0;
        const endHeight = prevHeight + heightOfPage + gap;
        documentHeight += heightOfPage;

        return [
          ...accumulator,
          [prevHeight, endHeight],
        ];
      }, []);

      setPagesIntervals(pagesHeightIntervals);      
      setOverallHeight(documentHeight);

      if (heightOfparent >= documentHeight) setIsAvailableAccept && setIsAvailableAccept(true);
    }
    return() => {
      setIsAvailableAccept && setIsAvailableAccept(false);
      setOverallHeight && setOverallHeight(0);
    }
  },[pages.length, scale]);

  React.useEffect(() => {
    if (pages[0]) {
      const widthOfparent = Number(windowRef.current?.props.width);
      const documentWidth = pages[0]?.getViewport({ scale })?.width;

      if (documentWidth > widthOfparent) {
        const ratio = Number(documentWidth/widthOfparent);
        const persectScale = Number((1 / ratio).toFixed(1)) - 0.05;
        setScale(persectScale);
      }
    }
  },[pages])

  const fetchPage = React.useCallback(
    async (index: number) => {
      if (pages[index]) return;

      const page = await handleGetPdfPage(index);
      setPages((prev) => {
        const next = [...prev];
        next[index] = page;
        return next;
      });

      windowRef.current?.resetAfterIndex(index);
    },
    [handleGetPdfPage, pages]
  );

  const handleItemSize = (index: number) => {
    const page = pages[index];
    if (page) {
      const viewport = page.getViewport({ scale });
      const itemSize = viewport.height + gap;
      return itemSize;
    }
    return 50;
  };

  const scrollDocument = React.useCallback((e: ListOnScrollProps) => {
    const payloadHeight = Math.abs(overallHeight! - Number(height) - pages.length);
    const correctPageIndex = pagesIntervals.findIndex(interval => {
      const start = interval[0].toFixed();
      const end = interval[1].toFixed();
      return Number(e.scrollOffset.toFixed()) >= Number(start) && Number(e.scrollOffset.toFixed()) < Number(end)
    });
    
    setCurrentPage && setCurrentPage(correctPageIndex !== -1 ? correctPageIndex + 1 : 1);
    
    if (e.scrollOffset >= Math.abs(payloadHeight)) {
      setIsAvailableAccept && setIsAvailableAccept(true);
    }
  }, [height, pagesIntervals, scale]);

  const VariableSizeListAny = VariableSizeList as any;

  return (
    <VariableSizeListAny
      onScroll={scrollDocument}
      ref={windowRef}
      width={width}
      height={height}
      itemCount={pagesCount}
      itemSize={handleItemSize}
    >
      {({ index, style }: any) => {
        fetchPage(index);
        return (
          <Page style={style}>
            <PdfPage style={style} page={pages[index]} scale={scale} isFlexStart={isFlexStart}/>
          </Page>
        );
      }}
    </VariableSizeListAny>
  );
};

export default Viewer;
